import { css, useTheme } from '@emotion/react';
import { ChevronRight, Pause, Play } from 'lucide-react';
import { TONE_COLORS, TONES, WPM_PRESETS } from '@sonematic/shared';
import type { ParsedSection, Tone } from '@sonematic/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { GenerationPartitionStatus, GenerationSectionStatus } from '@/lib/api-client';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

export interface DisplaySection extends ParsedSection {
  detectedTone?: string | null;
  toneConfidence?: number | null;
  generationStatus?: string | null;
  audioOffsetMs?: number | null;
}

export interface SectionPreviewProps {
  sections: DisplaySection[];
  globalWpm: number;
  sectionWpmOverrides: Map<number, number | null>;
  onSectionWpmChange: (position: number, wpmOverride: number | null) => void;
  sectionToneOverrides?: Map<number, string | null>;
  onSectionToneChange?: (position: number, tone: string | null) => void;
  generatedSections?: GenerationSectionStatus[];
  partitions?: GenerationPartitionStatus[];
  timingEstimated?: boolean;
  externalCollapseAll?: boolean;
  onAllCollapsedChange?: (allCollapsed: boolean) => void;
  musicOnly?: boolean;
  hideWpm?: boolean;
}

const MAX_PREVIEW_LENGTH = 200;

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

function getStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'generating':
    case 'analyzing_tone':
      return 'Generating';
    case 'ready':
      return 'Ready';
    case 'error':
      return 'Error';
    default:
      return 'Not generated';
  }
}

function getStatusColor(status: string | null | undefined, colors: Record<string, string>): string {
  switch (status) {
    case 'ready':
      return colors.success;
    case 'generating':
    case 'analyzing_tone':
      return colors.primary;
    case 'error':
      return colors.error;
    default:
      return colors.textSecondary;
  }
}

function ToneBadge({
  tone,
  confidence,
  position,
  onToneChange,
}: {
  tone: string;
  confidence: number | null;
  position?: number;
  onToneChange?: (position: number, tone: string) => void;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const GAP = 4;

  const updateMenuPos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Close if trigger has scrolled fully out of viewport vertically
    const isOffscreen = rect.height > 0 && (rect.bottom <= 0 || rect.top >= window.innerHeight);
    if (isOffscreen) {
      setOpen(false);
      return;
    }
    const menuEl = menuRef.current;
    const menuHeight = menuEl ? menuEl.offsetHeight : 0;
    const spaceBelow = window.innerHeight - rect.bottom - GAP;
    setMenuPos({
      top: spaceBelow >= menuHeight ? rect.bottom + GAP : rect.top - GAP - menuHeight,
      left: rect.left,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    // Re-anchor on any scroll (including nested scrollable containers)
    document.addEventListener('scroll', updateMenuPos, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', updateMenuPos, true);
    };
  }, [open, updateMenuPos]);

  const menuCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (node) {
        updateMenuPos();
      }
    },
    [updateMenuPos],
  );

  const label = confidence != null ? `${tone} ${confidence}%` : capitalize(tone);
  const toneColor = TONE_COLORS[tone as Tone] ?? {
    bg: theme.colors.primary,
    fg: theme.colors.text,
  };

  if (!onToneChange || position === undefined) {
    return (
      <span
        css={css`
          font-size: ${fontSizes.xs};
          color: ${toneColor.fg};
          background: ${toneColor.bg};
          padding: 2px ${spacing.sm};
          border-radius: ${radii.lg};
          font-weight: 600;
        `}
      >
        {label}
      </span>
    );
  }

  const handleOpen = () => {
    if (!open) {
      updateMenuPos();
    }
    setOpen(!open);
  };

  return (
    <div
      css={css`
        position: relative;
      `}
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-label={`Tone: ${tone}`}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
          }
        }}
        css={css`
          font-size: ${fontSizes.xs};
          color: ${toneColor.fg};
          background: ${toneColor.bg};
          padding: 2px ${spacing.sm};
          border-radius: ${radii.lg};
          border: none;
          cursor: pointer;
          font-weight: 600;
          transition: opacity 0.15s ease;

          &:hover {
            opacity: 0.8;
          }
        `}
      >
        {label}
      </span>
      {open &&
        createPortal(
          <div
            ref={menuCallbackRef}
            role="menu"
            css={css`
              position: fixed;
              top: ${menuPos.top}px;
              left: ${menuPos.left}px;
              background: ${theme.colors.surface};
              border: 1px solid ${theme.colors.border};
              border-radius: ${radii.md};
              padding: ${spacing.xs};
              z-index: 1000;
              min-width: 130px;
              box-shadow: ${shadows.elevated};
            `}
          >
            {TONES.map((t) => (
              <button
                key={t}
                type="button"
                role="menuitem"
                onClick={() => {
                  onToneChange(position, t);
                  setOpen(false);
                }}
                css={css`
                  display: block;
                  width: 100%;
                  padding: ${spacing.xs} ${spacing.sm};
                  border: none;
                  background: ${tone === t ? theme.colors.border : 'transparent'};
                  color: ${theme.colors.text};
                  font-size: ${fontSizes.sm};
                  text-align: left;
                  cursor: pointer;
                  border-radius: ${radii.sm};

                  &:hover {
                    background: ${theme.colors.border};
                  }
                `}
              >
                {capitalize(t)}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

function WpmBadge({
  position,
  globalWpm,
  wpmOverride,
  onWpmChange,
}: {
  position: number;
  globalWpm: number;
  wpmOverride: number | null;
  onWpmChange: (position: number, wpmOverride: number | null) => void;
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const effectiveWpm = wpmOverride ?? globalWpm;
  const isOverride = wpmOverride !== null && wpmOverride !== undefined;

  const handleSelect = (value: number | null) => {
    onWpmChange(position, value);
    setOpen(false);
  };

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <button
        type="button"
        aria-label={`${effectiveWpm} WPM${isOverride ? ' (override)' : ''}`}
        onClick={() => setOpen(!open)}
        css={css`
          font-size: ${fontSizes.xs};
          color: ${isOverride ? theme.colors.surface : theme.colors.textSecondary};
          background: ${isOverride ? theme.colors.text : theme.colors.border};
          padding: 2px ${spacing.sm};
          border-radius: ${radii.lg};
          border: none;
          cursor: pointer;
          font-weight: ${isOverride ? 600 : 400};
          transition: all 0.15s ease;

          &:hover {
            opacity: 0.8;
          }
        `}
      >
        {effectiveWpm} WPM
      </button>
      {open && (
        <div
          role="menu"
          css={css`
            position: absolute;
            top: 100%;
            right: 0;
            margin-top: ${spacing.xs};
            background: ${theme.colors.surface};
            border: 1px solid ${theme.colors.border};
            border-radius: ${radii.md};
            padding: ${spacing.xs};
            z-index: 10;
            min-width: 120px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          `}
        >
          {WPM_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              role="menuitem"
              onClick={() => handleSelect(preset.value)}
              css={css`
                display: block;
                width: 100%;
                padding: ${spacing.xs} ${spacing.sm};
                border: none;
                background: ${effectiveWpm === preset.value ? theme.colors.border : 'transparent'};
                color: ${theme.colors.text};
                font-size: ${fontSizes.sm};
                text-align: left;
                cursor: pointer;
                border-radius: ${radii.sm};

                &:hover {
                  background: ${theme.colors.border};
                }
              `}
            >
              {preset.label} ({preset.value})
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={() => handleSelect(null)}
            css={css`
              display: block;
              width: 100%;
              padding: ${spacing.xs} ${spacing.sm};
              border: none;
              background: ${!isOverride ? theme.colors.border : 'transparent'};
              color: ${theme.colors.textSecondary};
              font-size: ${fontSizes.sm};
              text-align: left;
              cursor: pointer;
              border-radius: ${radii.sm};

              &:hover {
                background: ${theme.colors.border};
              }
            `}
          >
            Use Global ({globalWpm})
          </button>
        </div>
      )}
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function SectionPreview({
  sections,
  globalWpm,
  sectionWpmOverrides,
  onSectionWpmChange,
  onSectionToneChange,
  generatedSections,
  partitions,
  timingEstimated: _timingEstimated,
  externalCollapseAll,
  onAllCollapsedChange,
  musicOnly,
  hideWpm,
}: SectionPreviewProps) {
  const theme = useTheme();

  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());

  // In musicOnly mode, collapse all sections initially
  const musicOnlyInitRef = useRef(false);
  useEffect(() => {
    if (musicOnly && !musicOnlyInitRef.current && sections.length > 0) {
      musicOnlyInitRef.current = true;
      setCollapsedSections(new Set(sections.map((s) => s.position)));
    }
  }, [musicOnly, sections]);
  const [playingPosition, setPlayingPosition] = useState<number | null>(null);
  const [sectionProgress, setSectionProgress] = useState(0);
  const sectionAudioRefs = useRef<Map<number, HTMLAudioElement>>(new Map());

  const generatedMap = useMemo(
    () =>
      new Map(
        (generatedSections ?? [])
          .filter((s) => s.generationStatus === 'ready' && (s.audioUrl || s.audioOffsetMs != null))
          .map((s) => [s.position, s]),
      ),
    [generatedSections],
  );

  // Build partition audio refs keyed by partition audioUrl for shared playback
  const partitionAudioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  // Map section position → partition audioUrl for partition-mode sections
  const sectionPartitionMap = useMemo(() => {
    const map = new Map<number, string>();
    if (!partitions || partitions.length === 0) return map;
    const partitionByIndex = new Map(
      partitions
        .filter((p) => p.status === 'ready' && p.audioUrl)
        .map((p) => [p.partitionIndex, p.audioUrl!]),
    );
    (generatedSections ?? []).forEach((s) => {
      if (s.audioOffsetMs != null && s.generationStatus === 'ready' && s.partitionIndex != null) {
        const url = partitionByIndex.get(s.partitionIndex);
        if (url) map.set(s.position, url);
      }
    });
    return map;
  }, [partitions, generatedSections]);

  // Track active section boundary for auto-pause in partition mode
  const activeSectionBoundary = useRef<{ position: number; endTimeSec: number } | null>(null);

  // Clean up audio elements on unmount
  useEffect(() => {
    const sectionRefs = sectionAudioRefs.current;
    const partitionRefs = partitionAudioRefs.current;
    return () => {
      sectionRefs.forEach((audio) => audio.pause());
      sectionRefs.clear();
      partitionRefs.forEach((audio) => audio.pause());
      partitionRefs.clear();
    };
  }, []);

  const handleSectionPlayPause = useCallback(
    (position: number, audioUrl: string) => {
      const genSection = generatedMap.get(position);
      const partitionUrl = sectionPartitionMap.get(position);
      const isPartitionMode = partitionUrl != null && genSection?.audioOffsetMs != null;

      // Pause all other audio
      sectionAudioRefs.current.forEach((audio, pos) => {
        if (pos !== position) audio.pause();
      });
      partitionAudioRefs.current.forEach((audio) => audio.pause());

      if (isPartitionMode) {
        // Partition mode: use shared partition audio element
        let audio = partitionAudioRefs.current.get(partitionUrl);
        if (!audio) {
          audio = new Audio(partitionUrl);
          audio.addEventListener('ended', () => {
            setPlayingPosition(null);
            setSectionProgress(0);
            activeSectionBoundary.current = null;
          });
          audio.addEventListener('timeupdate', () => {
            if (!audio!.duration) return;
            const boundary = activeSectionBoundary.current;
            if (boundary && audio!.currentTime >= boundary.endTimeSec) {
              audio!.pause();
              setPlayingPosition(null);
              setSectionProgress(1);
              activeSectionBoundary.current = null;
              return;
            }
            // Calculate progress within the section's offset range
            const offsetSec = (genSection!.audioOffsetMs ?? 0) / 1000;
            const durationSec = (genSection!.audioDurationMs ?? 0) / 1000;
            if (durationSec > 0) {
              const sectionProgress = (audio!.currentTime - offsetSec) / durationSec;
              setSectionProgress(Math.max(0, Math.min(1, sectionProgress)));
            }
          });
          partitionAudioRefs.current.set(partitionUrl, audio);
        }

        if (playingPosition === position) {
          audio.pause();
          setPlayingPosition(null);
          activeSectionBoundary.current = null;
        } else {
          const offsetSec = genSection!.audioOffsetMs! / 1000;
          const durationSec = (genSection!.audioDurationMs ?? 0) / 1000;
          audio.currentTime = offsetSec;
          activeSectionBoundary.current = {
            position,
            endTimeSec: offsetSec + durationSec,
          };
          setSectionProgress(0);
          audio.play().catch(() => setPlayingPosition(null));
          setPlayingPosition(position);
        }
      } else {
        // Per-section mode: use per-section audio element (existing behavior)
        let audio = sectionAudioRefs.current.get(position);
        if (!audio) {
          audio = new Audio(audioUrl);
          audio.addEventListener('ended', () => {
            setPlayingPosition(null);
            setSectionProgress(0);
          });
          audio.addEventListener('timeupdate', () => {
            if (audio!.duration) {
              setSectionProgress(audio!.currentTime / audio!.duration);
            }
          });
          sectionAudioRefs.current.set(position, audio);
        }

        if (playingPosition === position) {
          audio.pause();
          setPlayingPosition(null);
        } else {
          setSectionProgress(audio.duration ? audio.currentTime / audio.duration : 0);
          audio.play().catch(() => setPlayingPosition(null));
          setPlayingPosition(position);
        }
      }
    },
    [playingPosition, generatedMap, sectionPartitionMap],
  );

  const handleSectionScrub = useCallback(
    (position: number, fraction: number) => {
      const genSection = generatedMap.get(position);
      const partitionUrl = sectionPartitionMap.get(position);
      const isPartitionMode = partitionUrl != null && genSection?.audioOffsetMs != null;

      if (isPartitionMode) {
        const audio = partitionAudioRefs.current.get(partitionUrl);
        if (audio && audio.duration) {
          const offsetSec = genSection!.audioOffsetMs! / 1000;
          const durationSec = (genSection!.audioDurationMs ?? 0) / 1000;
          audio.currentTime = offsetSec + fraction * durationSec;
          setSectionProgress(fraction);
        }
      } else {
        const audio = sectionAudioRefs.current.get(position);
        if (audio && audio.duration) {
          audio.currentTime = fraction * audio.duration;
          setSectionProgress(fraction);
        }
      }
    },
    [generatedMap, sectionPartitionMap],
  );

  const allCollapsed = sections.length > 0 && collapsedSections.size === sections.length;

  const toggleSection = (position: number) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(position)) {
        next.delete(position);
      } else {
        next.add(position);
      }
      return next;
    });
  };

  const toggleAll = useCallback(() => {
    if (allCollapsed) {
      setCollapsedSections(new Set());
    } else {
      setCollapsedSections(new Set(sections.map((s) => s.position)));
    }
  }, [allCollapsed, sections]);

  // Allow parent to trigger collapse/expand all via toggle
  const collapseAllCount = useRef(0);
  const prevToggle = useRef(externalCollapseAll);
  useEffect(() => {
    if (prevToggle.current !== externalCollapseAll) {
      prevToggle.current = externalCollapseAll;
      // Skip the initial render
      if (collapseAllCount.current > 0) {
        toggleAll();
      }
      collapseAllCount.current++;
    }
  }, [externalCollapseAll, toggleAll]);

  // Report allCollapsed state to parent
  useEffect(() => {
    onAllCollapsedChange?.(allCollapsed);
  }, [allCollapsed, onAllCollapsedChange]);

  if (sections.length === 0) {
    return (
      <div
        css={css`
          padding: ${spacing.lg};
          text-align: center;
          color: ${theme.colors.textSecondary};
          font-size: ${fontSizes.sm};
        `}
      >
        <p>No sections yet.</p>
        <p>
          Write your script and click Analyze to detect sections, or use --- to separate them
          manually.
        </p>
      </div>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.md};
      `}
    >
      {sections.length > 1 && externalCollapseAll === undefined && (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
          `}
        >
          <button
            type="button"
            onClick={toggleAll}
            css={css`
              font-size: ${fontSizes.xs};
              color: ${theme.colors.textSecondary};
              background: transparent;
              border: none;
              cursor: pointer;
              padding: ${spacing.xs} ${spacing.sm};
              border-radius: ${radii.sm};

              &:hover {
                color: ${theme.colors.text};
                background: ${theme.colors.border};
              }
            `}
          >
            {allCollapsed ? 'Expand All' : 'Collapse All'}
          </button>
        </div>
      )}
      {sections.map((section) => {
        const wpmOverride = sectionWpmOverrides.get(section.position) ?? null;
        const isCollapsed = collapsedSections.has(section.position);
        const statusLabel = getStatusLabel(section.generationStatus);
        const statusColor = getStatusColor(
          section.generationStatus,
          theme.colors as unknown as Record<string, string>,
        );

        return (
          <div
            key={section.position}
            css={css`
              border: 1px solid ${theme.colors.border};
              border-radius: ${radii.md};
              background: ${theme.colors.surface};
              overflow: hidden;
            `}
          >
            {/* Header — clickable to toggle */}
            <button
              type="button"
              aria-label={isCollapsed ? `Expand ${section.title}` : `Collapse ${section.title}`}
              onClick={() => toggleSection(section.position)}
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${spacing.xs};
                width: 100%;
                padding: ${spacing.sm} ${spacing.md};
                border: none;
                background: transparent;
                cursor: pointer;
                text-align: left;
                color: inherit;
              `}
            >
              {musicOnly ? (
                /* Music-only header: Section N + tone badge right-aligned */
                <div
                  data-testid="section-header-title"
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: ${spacing.sm};
                  `}
                >
                  <ChevronRight
                    size={14}
                    aria-hidden="true"
                    css={css`
                      flex-shrink: 0;
                      color: ${theme.colors.textSecondary};
                      transform: rotate(${isCollapsed ? '0deg' : '90deg'});
                      transition: transform 0.15s;
                    `}
                  />
                  <span
                    css={css`
                      font-size: ${fontSizes.sm};
                      font-weight: 600;
                      color: ${theme.colors.text};
                    `}
                  >
                    Section {section.position + 1}
                  </span>
                  {section.detectedTone && (
                    <span
                      css={css`
                        margin-left: auto;
                      `}
                    >
                      <ToneBadge
                        tone={section.detectedTone}
                        confidence={section.toneConfidence ?? null}
                        position={section.position}
                        onToneChange={onSectionToneChange}
                      />
                    </span>
                  )}
                </div>
              ) : (
                <>
                  {/* Row 1: Chevron + Title */}
                  <div
                    data-testid="section-header-title"
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${spacing.sm};
                    `}
                  >
                    <ChevronRight
                      size={14}
                      aria-hidden="true"
                      css={css`
                        flex-shrink: 0;
                        color: ${theme.colors.textSecondary};
                        transform: rotate(${isCollapsed ? '0deg' : '90deg'});
                        transition: transform 0.15s;
                      `}
                    />
                    <h3
                      css={css`
                        margin: 0;
                        font-size: ${fontSizes.md};
                        color: ${theme.colors.text};
                        flex: 1;
                        min-width: 0;
                      `}
                    >
                      {section.title}
                    </h3>
                    <span
                      css={css`
                        font-size: ${fontSizes.xs};
                        color: ${theme.colors.textSecondary};
                        font-weight: 400;
                        white-space: nowrap;
                      `}
                    >
                      {section.wordCount} words
                    </span>
                  </div>

                  {/* Row 2: Badge chips */}
                  <div
                    data-testid="section-header-badges"
                    css={css`
                      display: flex;
                      flex-wrap: wrap;
                      gap: ${spacing.xs};
                      padding-left: 22px;
                    `}
                  >
                    {/* Status badge — hidden for default "Not generated" */}
                    {statusLabel !== 'Not generated' && (
                      <span
                        css={css`
                          font-size: ${fontSizes.xs};
                          color: ${statusColor};
                          padding: 2px ${spacing.sm};
                          border-radius: ${radii.lg};
                          border: 1px solid ${statusColor};
                          white-space: nowrap;
                        `}
                      >
                        {statusLabel}
                      </span>
                    )}

                    {section.detectedTone && (
                      <ToneBadge
                        tone={section.detectedTone}
                        confidence={section.toneConfidence ?? null}
                        position={section.position}
                        onToneChange={onSectionToneChange}
                      />
                    )}
                  </div>
                </>
              )}
            </button>

            {/* Collapsible content */}
            {!isCollapsed && (
              <div
                css={css`
                  padding: 0 ${spacing.md} ${spacing.md};
                `}
              >
                {!musicOnly && section.content && (
                  <p
                    css={css`
                      margin: 0 0 ${spacing.md};
                      font-size: ${fontSizes.sm};
                      color: ${theme.colors.textSecondary};
                      line-height: 1.5;
                      white-space: pre-wrap;
                    `}
                  >
                    {truncate(section.content, MAX_PREVIEW_LENGTH)}
                  </p>
                )}
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: ${spacing.md};
                  `}
                >
                  {!musicOnly && !hideWpm && (
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: ${spacing.sm};
                      `}
                    >
                      <span
                        css={css`
                          font-size: ${fontSizes.sm};
                          color: ${theme.colors.textSecondary};
                          min-width: 100px;
                        `}
                      >
                        Speaking Speed
                      </span>
                      <WpmBadge
                        position={section.position}
                        globalWpm={globalWpm}
                        wpmOverride={wpmOverride}
                        onWpmChange={onSectionWpmChange}
                      />
                    </div>
                  )}
                  {/* Section audio player */}
                  {generatedMap.has(section.position) && (
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: ${spacing.sm};
                        padding: ${spacing.sm} 0;
                        border-top: 1px solid ${theme.colors.border};
                        margin-top: ${spacing.xs};
                      `}
                    >
                      <span
                        css={css`
                          font-size: ${fontSizes.sm};
                          color: ${playingPosition === section.position
                            ? theme.colors.primary
                            : theme.colors.textSecondary};
                          flex-shrink: 0;
                        `}
                      >
                        Preview section music
                      </span>
                      <div
                        css={css`
                          flex: 1;
                        `}
                      />
                      {playingPosition === section.position && (
                        <div
                          role="slider"
                          aria-label={`Playback progress ${section.title}`}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={Math.round(sectionProgress * 100)}
                          tabIndex={0}
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const fraction = Math.max(
                              0,
                              Math.min(1, (e.clientX - rect.left) / rect.width),
                            );
                            handleSectionScrub(section.position, fraction);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowRight') {
                              handleSectionScrub(
                                section.position,
                                Math.min(1, sectionProgress + 0.05),
                              );
                            } else if (e.key === 'ArrowLeft') {
                              handleSectionScrub(
                                section.position,
                                Math.max(0, sectionProgress - 0.05),
                              );
                            }
                          }}
                          css={css`
                            height: 4px;
                            background: ${theme.colors.border};
                            border-radius: 2px;
                            cursor: pointer;
                            position: relative;
                            max-width: 100px;
                            width: 100px;
                          `}
                        >
                          <div
                            css={css`
                              position: absolute;
                              top: 0;
                              left: 0;
                              height: 100%;
                              width: ${sectionProgress * 100}%;
                              background: ${theme.colors.primary};
                              border-radius: 2px;
                              transition: width 0.1s linear;
                            `}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          handleSectionPlayPause(
                            section.position,
                            generatedMap.get(section.position)!.audioUrl ?? '',
                          )
                        }
                        aria-label={
                          playingPosition === section.position
                            ? `Pause ${section.title}`
                            : `Play ${section.title}`
                        }
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          width: 28px;
                          height: 28px;
                          border-radius: 50%;
                          border: none;
                          background: transparent;
                          color: ${playingPosition === section.position
                            ? theme.colors.primary
                            : theme.colors.textSecondary};
                          cursor: pointer;
                          flex-shrink: 0;
                          transition: color 0.15s ease;
                          margin-left: auto;

                          &:hover {
                            color: ${theme.colors.text};
                          }
                        `}
                      >
                        {playingPosition === section.position ? (
                          <Pause size={16} aria-hidden="true" />
                        ) : (
                          <Play size={16} aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
